import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from '../../../components/select';
import Input from '../../../components/input';
import Modal from '../../../components/modal';
import close from '../../../assets/svg-icons/closeBrand.svg';
import deleteIcon from '../../../assets/svg-icons/deleteRed.svg';
import { useDBServerList, useListDews } from '../layout/resuableEffect';
import {
  createServer,
  getDBServerStatus,
  getUserVirtualAccount,
  getMigrationStatus,
  listDBServers,
  deleteServer,
} from '../../../hooks/local/userReducer';
import PaymentModal from '../../../components/paymentModal';
import { toast } from 'react-toastify';
import {
  CircleCheckBig,
  Copy,
  Eye,
  EyeOff,
  AlertTriangle,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';

const ManagedDatabases = () => {
  document.title = 'Managed Databases - CloudVantage';

  const dispatch = useDispatch();
  const projectId = useSelector((state) => state.user.selectedProject)?.id;
  const currency = useSelector((state) => state.user.currency);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === 'USD'
        ? Number(state.user.virtualAccount.dollar_balance) || 0
        : Number(state.user.virtualAccount.balance) || 0
      : 0,
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);

  const [flavorModal, setFlavorModal] = useState(false);
  const [configModal, setConfigModal] = useState(false);
  const [selectedFlavor, setSelectedFlavor] = useState('');
  const [deleteDatabaseModal, setDeleteDatabaseModal] = useState(false);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPaymentForMigration, setIsPaymentForMigration] = useState(false);
  const [migrationSuccessModal, setMigrationSuccessModal] = useState(false);

  // Migration states

  const [migrateFlavorModal, setMigrateFlavorModal] = useState(false);
  const [migrateConfigModal, setMigrateConfigModal] = useState(false);
  const [selectedMigrateFlavor, setSelectedMigrateFlavor] = useState('');
  const [migrationStep, setMigrationStep] = useState(1);

  // Database connection check states
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [connectionCheckStatus, setConnectionCheckStatus] = useState(null); // 'PENDING', 'SUCCESS', 'FAILED'
  const [databaseInfo, setDatabaseInfo] = useState(null);
  const [connectionCheckError, setConnectionCheckError] = useState(null);

  // Enhanced states for provisioning and credentials
  const [credentialsModal, setCredentialsModal] = useState(false);
  const [serverCredentials, setServerCredentials] = useState(null);
  const [credentialsVisibility, setCredentialsVisibility] = useState({
    password: false,
  });

  // Manage dropdown and modal states
  const [openManageDropdown, setOpenManageDropdown] = useState(null);
  const [selectedDbForCredentials, setSelectedDbForCredentials] =
    useState(null);

  // Default values for migration form
  const defaultMigrationValues = {
    databaseEngine: 'MySQL',
    databaseName: '',
    databaseType: 'mysql',
    databaseUsername: '',
    databasePassword: '',
    confirmDatabasePassword: '',
    guiChoice: '',
    payment: '',
    flavor_id: '',
    migrate_db: 'yes',
    source_db_ip: '',
    source_db_port: '3306',
    source_db_user: '',
    source_db_password: '',
    migration_type: '',
  };

  const [migrationInitialValues, setMigrationInitialValues] = useState(
    defaultMigrationValues,
  );
  const [errorDetailsModal, setErrorDetailsModal] = useState(false);
  const [selectedDbForError, setSelectedDbForError] = useState(null);
  const [securityFeedbackModal, setSecurityFeedbackModal] = useState(false);
  const [selectedDbForSecurity, setSelectedDbForSecurity] = useState(null);

  // Refs
  const dropdownRef = useRef(null);
  const selectedGuiChoiceRef = useRef(null);

  const entriesPerPage = 5;

  const vpsList = useListDews();
  const vpsMapList = vpsList?.results || [];

  const initialDbServersList = useDBServerList();
  const [dbServersList, setDbServersList] = useState([]);

  useEffect(() => {
    setDbServersList(initialDbServersList);
  }, [initialDbServersList]);

  const isDbLimitReached = dbServersList.length >= 10;

  const options = [
    { value: 'all-db-servers', label: 'All db servers' },
    { value: 'name', label: 'Name' },
    { value: 'serverType', label: 'Server Type' },
    { value: 'status', label: 'Status' },
  ];

  const vpsOptions = vpsMapList.map((vps) => ({
    value: vps.flavor_id.toString(),
    label: `${vps.name} (${vps.ram} RAM, ${vps.vcpus} CPU, ${vps.disk} Storage) - ${
      currency === 'NGN' ? `N${vps.naira_price}/mo` : `$${vps.dollar_price}/mo`
    }`,
  }));

  // Auto-close Managed DB dropdown on scroll or click/touch anywhere
  useEffect(() => {
    const handleClose = (e) => {
      if (openManageDropdown !== null) {
        // Only close if the click is outside the dropdown container
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setOpenManageDropdown(null);
        }
      }
    };

    // Use capture for scroll to ensure it catches all scroll events
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('mousedown', handleClose);
    window.addEventListener('touchstart', handleClose);

    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('mousedown', handleClose);
      window.removeEventListener('touchstart', handleClose);
    };
  }, [openManageDropdown]);

  const flavorOptions = [
    { value: '', label: 'Select Flavor' },
    ...vpsOptions,
    { value: 'custom', label: 'Custom Flavor' },
  ];

  // Wrap dbServersListMap in useMemo to avoid unnecessary re-renders and fix lint warning
  const dbServersListMap = React.useMemo(() => {
    if (!Array.isArray(dbServersList)) return [];

    return dbServersList.map((db, index) => {
      // Extract IP address correctly from addresses object or accessIPv4
      const ip = db?.addresses?.External?.[0]?.addr || db?.accessIPv4 || 'N/A';

      // Map backend status to user-friendly dynamic status (DB Status)
      const displayStatus = db?.db_provisioning_status || 'Inactive';

      // Migration Status derived from db_migration_status + presence of migration task
      const rawMigStatus = db?.db_migration_status || '';
      const migrationTaskId =
        db?.migration_task_id ||
        db?.migration_task_taskId ||
        db?.db_details?.migration_task_id ||
        null;

      // Normalize migration status text for UI display
      let normalizedMigStatus = rawMigStatus;
      const migUpper = rawMigStatus?.toString().toUpperCase();

      if (!migrationTaskId && !rawMigStatus) {
        // No migration info at all
        normalizedMigStatus = 'N/A';
      } else if (!rawMigStatus && migrationTaskId) {
        // Task exists but no status yet
        normalizedMigStatus = 'In Progress';
      } else if (migUpper === 'RUNNING') {
        normalizedMigStatus = 'In Progress';
      } else if (migUpper === 'PENDING') {
        normalizedMigStatus = 'Pending';
      } else if (migUpper === 'SUCCESS' || migUpper === 'COMPLETED') {
        normalizedMigStatus = 'Completed';
      } else if (migUpper === 'FAILED' || migUpper === 'ERROR') {
        normalizedMigStatus = 'Failed';
      } else {
        // Fallback for any other strings
        normalizedMigStatus = rawMigStatus || 'N/A';
      }

      // One-time viewed status from localStorage if not in db object
      const localViewed =
        localStorage.getItem(`db_creds_viewed_${db.id}`) === 'true';

      return {
        sn: index + 1,
        id: db?.id,
        name: db?.name || 'N/A',
        ip: ip,
        engine: 'MySQL',
        serverType: db?.db_server_type || 'N/A',
        status: displayStatus,
        migrationStatus: normalizedMigStatus,
        migrationTaskId,
        dbDetails: db?.db_details || null,
        errorMessage:
          db?.error_message || db?.db_details?.error_message || null,
        credentialsViewed: db?.credentials_viewed || localViewed,
      };
    });
  }, [dbServersList]);

  // EFFECT: Background status polling for table auto-refresh
  useEffect(() => {
    if (!projectId) return;

    const interval = setInterval(async () => {
      try {
        const response = await apiEndPoints.listServersDB(projectId);
        const servers = response.data?.servers || [];
        setDbServersList(servers);
      } catch (error) {
        console.error('Background fetch error:', error);
      }
    }, 3000); // Poll every 3 seconds for table updates

    return () => clearInterval(interval);
  }, [projectId]);

  // Migration form with multi-step validation
  const migrateDatabase = useFormik({
    initialValues: migrationInitialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      databaseName: Yup.string().matches(
        /^[a-zA-Z0-9_]+$/,
        'Database name can only contain letters, numbers, and underscores',
      ),
      databaseType: Yup.string(),
      databaseUsername: Yup.string(),
      databasePassword: Yup.string().min(
        8,
        'Password must be at least 8 characters',
      ),
      confirmDatabasePassword: Yup.string().oneOf(
        [Yup.ref('databasePassword'), null],
        'Passwords must match',
      ),
      guiChoice: Yup.string(),
      payment: Yup.string(),
      flavor_id: Yup.mixed(),
      source_db_ip: Yup.string()
        .required('Source database IP is required')
        .matches(/^(\d{1,3}\.){3}\d{1,3}$/, 'Please enter a valid IP address'),
      source_db_port: Yup.number()
        .required('Source database port is required')
        .min(1, 'Port must be at least 1')
        .max(65535, 'Port must be at most 65535'),
      source_db_user: Yup.string().required(
        'Source database username is required',
      ),
      source_db_password: Yup.string().required(
        'Source database password is required',
      ),
      migration_type: Yup.string()
        .oneOf(
          ['test', 'live'],
          "Migration type must be either 'test' or 'live'",
        )
        .required('Migration type is required'),
    }),
    onSubmit: async (values) => {
      const foundFlavor = vpsMapList.find(
        (vps) => vps.flavor_id === Number(values.flavor_id),
      );

      // Store GUI choice for fallback credentials
      selectedGuiChoiceRef.current = values.guiChoice;

      const estimate = calculateEstimatedCost(
        values.flavor_id,
        currency,
        values.payment,
      ).numeric;

      // Students are not billed, so we bypass the wallet check
      if (false) {
        setIsPaymentForMigration(true);
        setShowPaymentModal(true);
      } else {
        const createServerData = {
          flavor_id: Number(values.flavor_id),
          flavorRef: foundFlavor?.id || '',
          db_name: values.databaseName,
          db_type: values.databaseType,
          db_user: values.databaseUsername,
          db_password: values.databasePassword,
          gui_choice: values.guiChoice,
          payment_method: values.payment,
          db_preinstalled: 'yes',
          migrate_db: values.migrate_db,
          source_db_ip: values.source_db_ip,
          source_db_port: values.source_db_port,
          source_db_user: values.source_db_user,
          source_db_password: values.source_db_password,
          migration_type: values.migration_type,
          db_size: databaseInfo?.size_mb || 0, // Pass the DB size from the connection check
          projectId,
        };

        const { payload } = await dispatch(
          createServer({ createServerData, projectId }),
        );

        if (
          payload.statuscode === '00' &&
          payload.statusmessage === 'successful'
        ) {
          setMigrateConfigModal(false);

          // Refresh database list to show new DB with migration status
          await dispatch(listDBServers(projectId));

          // Refresh wallet balance after successful database creation
          await dispatch(getUserVirtualAccount());

          // Show success toast
          toast.success(
            'Database created successfully! Migration is running in the background.',
            { theme: 'colored' },
          );

          // Auto-reload to refresh the list and show the new DB
          setTimeout(() => {
            window.location.reload();
          }, 2000);

          // Reset form
          migrateDatabase.resetForm();
          setMigrationStep(1);
        } else {
          toast.error('Failed to create database server for migration');
        }
      }
    },
  });

  // Sync flavor_id to migration form whenever modal is open or flavor changes
  useEffect(() => {
    if (
      migrateConfigModal &&
      selectedMigrateFlavor &&
      migrateDatabase.values.flavor_id !== selectedMigrateFlavor.toString()
    ) {
      console.log('Syncing flavor_id to form:', selectedMigrateFlavor);
      migrateDatabase.setFieldValue(
        'flavor_id',
        selectedMigrateFlavor.toString(),
        false,
      );
    }

    // Ensure databaseType is always set
    if (migrateConfigModal && !migrateDatabase.values.databaseType) {
      migrateDatabase.setFieldValue('databaseType', 'mysql', false);
    }
  }, [
    migrateConfigModal,
    selectedMigrateFlavor,
    migrateDatabase.values.flavor_id,
    migrateDatabase.values.databaseType,
    migrateDatabase.setFieldValue,
    migrateDatabase,
  ]);

  const toggleFlavorModal = () => setFlavorModal(!flavorModal);
  const toggleConfigModal = () => {
  setConfigModal(!configModal);

  if (configModal) {
    createDatabase.resetForm();
  }
};
  const toggleDeleteDatabaseModal = () =>
    setDeleteDatabaseModal(!deleteDatabaseModal);

  // Enhanced toggle function for provisioning modal

  const handleSearchChange = (event) => {
    setSearchText(event.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const filteredDatabases = dbServersListMap.filter(
    (db) =>
      db?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      db?.serverType?.toLowerCase().includes(searchText.toLowerCase()) ||
      db?.status?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredDatabases.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const currentEntries = filteredDatabases.slice(
    startIndex,
    startIndex + entriesPerPage,
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const paymentOptions = [
    { value: '', label: 'Select an option' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  const calculateEstimatedCost = (flavorId, currency, paymentMethod) => {
    if (!flavorId || flavorId === 'custom' || !paymentMethod) {
      return { formatted: '0.00', numeric: 0 };
    }

    const selectedVps = vpsMapList.find(
      (vps) => vps.flavor_id === Number(flavorId),
    );
    if (!selectedVps) {
      return { formatted: '0.00', numeric: 0 };
    }

    const price =
      currency === 'NGN' ? selectedVps.naira_price : selectedVps.dollar_price;
    let totalCost = price;

    if (paymentMethod === 'yearly') {
      const yearlyPrice = totalCost * 12;
      const discount = yearlyPrice * 0.1;
      totalCost = yearlyPrice - discount;
    }

    const formatted = totalCost.toLocaleString('en-US', {
      style: 'currency',
      currency: currency === 'NGN' ? 'NGN' : 'USD',
    });

    return { formatted, numeric: totalCost };
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`${label} copied to clipboard`);
      })
      .catch(() => {
        toast.error('Failed to copy to clipboard');
      });
  };

  const copyAllCredentials = () => {
    if (!serverCredentials) return;

    let credentialsText = `Host: ${serverCredentials.host}
Port: ${serverCredentials.port}
Database: ${serverCredentials.database}
Username: ${serverCredentials.username}
Password: ${serverCredentials.password}`;

    // Add reset URL if it exists (for phpmyadmin)
    if (serverCredentials.resetUrl) {
      credentialsText += `\nReset URL: ${serverCredentials.resetUrl}`;
    }

    copyToClipboard(credentialsText, 'All credentials');
  };

  const openWebInterfaceUrl = () => {
    if (serverCredentials?.host) {
      // Ensure URL has protocol
      const url = serverCredentials.host.startsWith('http')
        ? serverCredentials.host
        : `http://${serverCredentials.host}`;
      window.open(url, '_blank');
    }
  };

  const createDatabase = useFormik({
    initialValues: {
      databaseEngine: 'MySQL',
      databaseName: '',
      databaseType: '',
      databaseUsername: '',
      databasePassword: '',
      confirmDatabasePassword: '',
      guiChoice: '',
      payment: '',
      flavor_id: '',
    },
    validationSchema: Yup.object({
      databaseName: Yup.string()
        .required('Database name is required')
        .matches(
          /^[a-zA-Z0-9_]+$/,
          'Database name can only contain letters, numbers, and underscores',
        ),
      databaseType: Yup.string().required('Database type is required'),
      databaseUsername: Yup.string().required('Database username is required'),
      databasePassword: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Database password is required'),
      confirmDatabasePassword: Yup.string()
        .oneOf([Yup.ref('databasePassword'), null], 'Passwords must match')
        .required('Please confirm your password'),
      guiChoice: Yup.string().required('GUI Choice is required'),
      payment: Yup.string().required('Payment method is required'),
      flavor_id: Yup.string().required('Flavor is required'),
    }),
    onSubmit: async (values) => {
      const foundFlavor = vpsMapList.find(
        (vps) => vps.flavor_id === Number(values.flavor_id),
      );

      // Store GUI choice for fallback credentials
      selectedGuiChoiceRef.current = values.guiChoice;

      const estimate = calculateEstimatedCost(
        values.flavor_id,
        currency,
        values.payment,
      ).numeric;

      // Students are not billed, so we bypass the wallet check
      if (false) {
        setShowPaymentModal(true);
      } else {
        const createServerData = {
          flavor_id: Number(values.flavor_id),
          flavorRef: foundFlavor?.id || '',
          db_name: values.databaseName,
          db_type: values.databaseType,
          db_user: values.databaseUsername,
          db_password: values.databasePassword,
          gui_choice: values.guiChoice,
          payment_method: values.payment,
          db_preinstalled: 'yes',
          projectId,
        };

        const { payload } = await dispatch(
          createServer({ createServerData, projectId }),
        );

        if (
          payload.statuscode === '00' &&
          payload.statusmessage === 'successful'
        ) {
          setConfigModal(false);

          // Refresh database list to show new DB with "In progress" status
          await dispatch(listDBServers(projectId));

          // Refresh wallet balance after successful database creation
          await dispatch(getUserVirtualAccount());

          // Show success toast
          toast.success('Database creation initiated successfully!', {
            theme: 'colored',
          });

          // Auto-reload to refresh the list and show the new DB
          setTimeout(() => {
            window.location.reload();
          }, 2000);

          // Reset form
          createDatabase.resetForm();
        } else {
          toast.error('Failed to create database server');
        }
      }
    },
  });

  // Migration form handlers
  const toggleMigrateFlavorModal = () =>
    setMigrateFlavorModal(!migrateFlavorModal);
  const toggleMigrateConfigModal = async (flavorId) => {
    const isOpening = !migrateConfigModal;

    if (isOpening) {
      // 1. Prepare data first
      const flavorToUse = (flavorId || selectedMigrateFlavor || '').toString();
      console.log(
        'Preparing to open migration modal with flavor:',
        flavorToUse,
      );

      // 2. Update initial values *before* opening to ensure Formik starts correctly
      setMigrationInitialValues({
        ...defaultMigrationValues,
        flavor_id: flavorToUse,
        databaseType: 'mysql',
        migrate_db: 'yes',
        source_db_port: '3306',
      });

      setMigrationStep(1);
      setConnectionCheckStatus(null);
      setDatabaseInfo(null);
      setConnectionCheckError(null);
      setIsCheckingConnection(false);

      // Force update form values to be sure
      migrateDatabase.setValues({
        ...defaultMigrationValues,
        flavor_id: flavorToUse,
        databaseType: 'mysql',
        migrate_db: 'yes',
        source_db_port: '3306',
      });

      // 3. Open modal
      setMigrateConfigModal(true);

      // 4. Cleanup/Refresh
      await dispatch(getUserVirtualAccount());
    } else {
      setMigrateConfigModal(false);
    }
  };

  // Handle database connection check
  const handleCheckDatabaseConnection = async () => {
    if (!projectId) {
      toast.error('Project ID is missing');
      return;
    }

    const values = migrateDatabase.values;

    // Validate required fields
    if (
      !values.source_db_ip ||
      !values.source_db_port ||
      !values.source_db_user ||
      !values.source_db_password ||
      !values.databaseName
    ) {
      toast.error(
        'Please fill in all source database fields and database name',
      );
      return;
    }

    setIsCheckingConnection(true);
    setConnectionCheckError(null);
    setDatabaseInfo(null);
    setConnectionCheckStatus(null);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('source_db_ip', values.source_db_ip);
      formData.append('source_db_port', values.source_db_port);
      formData.append('source_db_user', values.source_db_user);
      formData.append('db_name', values.databaseName);
      formData.append('source_db_password', values.source_db_password);

      // STEP 1: Initiate the connection check first
      console.log('Initiating database connection check...');
      const response = await apiEndPoints.checkDbConnect(projectId, formData);
      const responseData = response?.data || response;

      console.log('Initiate check response:', responseData);

      if (responseData.statuscode === '00' && responseData.task_id) {
        // Store the task_id from the initiation response
        const taskId = responseData.task_id;
        setConnectionCheckStatus('PENDING');
        setIsCheckingConnection(false); // Initiation is complete, now we're polling
        toast.success(
          'Database connection check initiated. Checking status...',
        );

        // STEP 2: Now start polling for status using the task_id
        console.log('Starting to poll for status with task_id:', taskId);
        startPollingConnectionStatus(taskId);
      } else {
        setIsCheckingConnection(false);
        setConnectionCheckStatus('FAILED');
        setConnectionCheckError(
          responseData.statusmessage ||
            responseData.message ||
            'Failed to initiate connection check',
        );
        toast.error(
          responseData.statusmessage ||
            responseData.message ||
            'Failed to initiate connection check',
        );
      }
    } catch (error) {
      console.error('Error initiating database connection check:', error);
      setIsCheckingConnection(false);
      setConnectionCheckStatus('FAILED');
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.statusmessage ||
        error?.message ||
        'Failed to initiate database connection check';
      setConnectionCheckError(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Start polling connection status (STEP 2: After initiation)
  const startPollingConnectionStatus = (taskId) => {
    if (!projectId || !taskId) {
      console.error('Cannot start polling: missing projectId or taskId');
      setConnectionCheckStatus('FAILED');
      setConnectionCheckError('Missing required parameters for status check');
      return;
    }

    const maxAttempts = 60; // Poll for up to 5 minutes (60 * 5 seconds)
    let attempts = 0;
    let shouldStop = false;

    console.log(
      `Starting to poll preflight check status for task_id: ${taskId}`,
    );

    const pollInterval = setInterval(async () => {
      if (shouldStop) {
        clearInterval(pollInterval);
        return;
      }

      attempts++;
      console.log(
        `Polling attempt ${attempts}/${maxAttempts} for task_id: ${taskId}`,
      );

      if (!projectId || !taskId) {
        shouldStop = true;
        clearInterval(pollInterval);
        return;
      }

      try {
        // STEP 2: Poll the status endpoint using the task_id from STEP 1
        const response = await apiEndPoints.getPreflightCheckStatus(
          projectId,
          taskId,
        );
        const responseData = response?.data || response;

        console.log(
          `Status check response (attempt ${attempts}):`,
          responseData,
        );

        if (responseData.statuscode === '00') {
          if (responseData.status === 'SUCCESS') {
            shouldStop = true;
            setConnectionCheckStatus('SUCCESS');
            setDatabaseInfo(responseData.database_info);
            setConnectionCheckError(null);
            toast.success('Database connection verified successfully!');
            clearInterval(pollInterval);
          } else if (responseData.status === 'FAILED') {
            shouldStop = true;
            setConnectionCheckStatus('FAILED');
            setConnectionCheckError(
              responseData.message ||
                responseData.statusmessage ||
                'Database connection test failed',
            );
            setDatabaseInfo(null);
            toast.error(
              responseData.message ||
                responseData.statusmessage ||
                'Database connection test failed',
            );
            clearInterval(pollInterval);
          }
          // If still PENDING, continue polling
        } else {
          shouldStop = true;
          setConnectionCheckStatus('FAILED');
          setConnectionCheckError(
            responseData.statusmessage ||
              responseData.message ||
              'Connection check failed',
          );
          setDatabaseInfo(null);
          clearInterval(pollInterval);
        }
      } catch (error) {
        console.error(
          `Error polling connection check status (attempt ${attempts}):`,
          error,
        );
        // Don't set failed status on every error, might be temporary
        const errorMsg = error?.response?.data?.message || error?.message;
        if (errorMsg && !errorMsg.includes('not found') && attempts > 3) {
          shouldStop = true;
          setConnectionCheckStatus('FAILED');
          setConnectionCheckError(errorMsg);
          clearInterval(pollInterval);
        }
      }

      // Stop if max attempts reached
      if (attempts >= maxAttempts && !shouldStop) {
        shouldStop = true;
        setConnectionCheckStatus('FAILED');
        setConnectionCheckError(
          'Connection check timed out. Please try again.',
        );
        toast.error('Connection check timed out');
        clearInterval(pollInterval);
      }
    }, 5000); // Poll every 5 seconds
  };

  const handleCredentialsModalClose = () => {
    if (selectedDbForCredentials) {
      // Mark as viewed in localStorage to hide the icon/button
      localStorage.setItem(
        `db_creds_viewed_${selectedDbForCredentials.id}`,
        'true',
      );
    }
    setCredentialsModal(false);
    setSelectedDbForCredentials(null);
    setServerCredentials(null);
    // Refresh list to update UI
    if (projectId) {
      dispatch(listDBServers(projectId));
    }
  };

  const deleteDatabase = async (databaseId) => {
    // console.log('Deleting database with ID:', databaseId);
    // toggleDeleteDatabaseModal();
    const { payload } = await dispatch(
      deleteServer({ projectId, serverId: databaseId }),
    );

    if (payload.status === 204 || payload.status === 200) {
      toast.success('Database server deleted successfully');
      setDeleteDatabaseModal(false);
      setSelectedDatabase(null);

      // Refresh database list to reflect any changes
      dispatch(listDBServers(projectId));

      // Reload the page after a short delay to allow toast to be seen
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  const handleMigrationSuccessModalClose = () => {
    setMigrationSuccessModal(false);
    // Show credentials modal after closing success modal
    setTimeout(() => {
      setCredentialsModal(true);
    }, 300);
  };

  // Handler for checking migration status manually
  const handleCheckMigrationStatus = async (db) => {
    setOpenManageDropdown(null);
    if (!db.migrationTaskId) {
      toast.error('No migration task found for this database');
      return;
    }

    try {
      const { payload } = await dispatch(
        getMigrationStatus({
          projectId,
          taskId: db.migrationTaskId,
        }),
      );

      if (payload?.statuscode === '00') {
        const statusMessage = `Migration Status: ${payload.status}`;
        toast.info(statusMessage, { theme: 'colored' });

        // Refresh database list to get updated status
        await dispatch(listDBServers(projectId));
      } else {
        toast.error('Failed to check migration status');
      }
    } catch (error) {
      console.error('Error checking migration status:', error);
      toast.error('Failed to check migration status');
    }
  };

  // Helper to normalize DB credentials from various backend formats
  const normalizeCredentials = (creds) => {
    if (!creds) return null;
    return {
      host: String(creds.db_host || creds.ip_address || creds.host || ''),
      port: String(creds.db_port || creds.port || '3306'),
      database: String(creds.db_name || creds.database || ''),
      username: String(creds.db_user || creds.username || ''),
      password: String(creds.db_password || creds.password || ''),
      resetUrl:
        creds.db_reset_url || creds.resetUrl
          ? String(creds.db_reset_url || creds.resetUrl)
          : null,
      guiType: creds.db_host ? 'phpmyadmin' : 'none',
      isPhpMyAdmin: !!creds.db_host,
    };
  };

  // Handler for viewing details (credentials or error)
  const handleViewDetails = async (db) => {
    setOpenManageDropdown(null);

    // Open credentials modal if the database is ACTIVE (now using "Completed" provisioning status)
    // AND Migration status is either 'COMPLETED'/'SUCCESS' or 'No migration' (checked via logic below)
    if (db.status === 'Completed') {
      console.log('Migration Status:', db.migrationStatus);
      const migStatus = db.migrationStatus?.toString().toUpperCase();
      const isMigrationComplete =
        migStatus === 'COMPLETED' ||
        migStatus === 'SUCCESS' ||
        migStatus === 'NO MIGRATION' ||
        migStatus === 'NO_MIGRATION' ||
        migStatus === 'N/A' ||
        !migStatus;

      // START: Enforce that we only show credentials if migration is complete or N/A
      if (!isMigrationComplete) {
        toast.info(
          'Credentials will be available once the migration is completed.',
          { theme: 'colored' },
        );
        return;
      }
      // END: Enforce strictly

      // Check if credentials have already been viewed
      if (db.credentialsViewed) {
        setSelectedDbForSecurity(db);
        setSecurityFeedbackModal(true);
        return;
      }

      let credentials = normalizeCredentials(db.dbDetails);

      // If credentials are missing, fetch them from the server on demand
      if (!credentials) {
        const loadingToast = toast.loading(
          'Fetching secure database credentials...',
          { theme: 'colored' },
        );
        try {
          const statusData = {
            projectId,
            serverId: db.id,
            serverIp: db.ip,
          };

          const { payload } = await dispatch(getDBServerStatus(statusData));
          const responseData = payload?.data || payload;
          const dbCreds = responseData?.db_creds;

          if (responseData.statuscode === '00' && dbCreds) {
            credentials = normalizeCredentials(dbCreds);

            toast.update(loadingToast, {
              render: 'Credentials retrieved successfully!',
              type: 'success',
              isLoading: false,
              autoClose: 2000,
              theme: 'colored',
            });
          } else {
            const errorMsg =
              responseData?.statusmessage ||
              'Failed to fetch database credentials';
            toast.update(loadingToast, {
              render: errorMsg,
              type: 'error',
              isLoading: false,
              autoClose: 3000,
              theme: 'colored',
            });
            return;
          }
        } catch (error) {
          console.error('Error fetching credentials:', error);
          toast.update(loadingToast, {
            render: 'Connection error while fetching credentials.',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
            theme: 'colored',
          });
          return;
        }
      }

      // Show credentials modal
      if (credentials) {
        setSelectedDbForCredentials(db);
        setServerCredentials(credentials);
        setCredentialsModal(true);
      }
    } else if (
      db.status === 'Failed' ||
      db.migrationStatus?.toString().toUpperCase() === 'FAILED'
    ) {
      // Show error details modal
      setSelectedDbForError(db);
      setErrorDetailsModal(true);
    }
  };

  // Refresh wallet balance when config modal opens
  // useEffect(() => {
  //   if (configModal) {
  //     dispatch(getUserVirtualAccount());
  //   }
  // }, [configModal, dispatch]);

  // Render main database status badge
  const renderStatus = (db) => {
    const rawStatus = db.status;
    const status = rawStatus ? rawStatus.toString().toUpperCase() : '';

    const statusConfig = {
      ACTIVE: {
        color:
          'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800/50',
        icon: <CircleCheckBig className="w-3.5 h-3.5" />,
        label: 'Active',
      },
      COMPLETED: {
        color:
          'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800/50',
        icon: <CircleCheckBig className="w-3.5 h-3.5" />,
        label: 'Completed',
      },
      'IN PROGRESS': {
        color:
          'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-200 dark:border-cyan-800/50',
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
        label: 'In progress',
      },
      FAILED: {
        color:
          'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800/50',
        icon: <AlertTriangle className="w-3.5 h-3.5" />,
        label: 'Failed',
      },
    };

    const config =
      statusConfig[status] || statusConfig[status.replace('_', ' ')];

    if (!config) {
      return (
        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-gray-50 text-gray-500 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-700 shadow-sm">
          {rawStatus}
        </span>
      );
    }

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${config.color}`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  // Render migration status badge
  const renderMigrationStatus = (db) => {
    const rawStatus = db.migrationStatus;

    // Normalize status for comparison
    const status = rawStatus ? rawStatus.toString().toUpperCase() : '';

    // Status configuration for badges
    const statusConfig = {
      RUNNING: {
        color:
          'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-200 dark:border-cyan-800/50',
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
        label: 'In progress',
      },
      INITIALIZING: {
        color:
          'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800/50',
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
        label: 'In progress',
      },
      PENDING: {
        color:
          'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800/50',
        icon: <Loader2 className="w-3.5 h-3.5 animate-pulse" />,
        label: 'Pending',
      },
      SUCCESS: {
        color:
          'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800/50',
        icon: <CircleCheckBig className="w-3.5 h-3.5" />,
        label: 'Completed',
      },
      COMPLETED: {
        color:
          'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800/50',
        icon: <CircleCheckBig className="w-3.5 h-3.5" />,
        label: 'Completed',
      },
      FAILED: {
        color:
          'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800/50',
        icon: <AlertTriangle className="w-3.5 h-3.5" />,
        label: 'Failed',
      },
    };

    // If status is "No migration" or empty, check for active task first
    if (
      status === 'NO MIGRATION' ||
      status === 'NO_MIGRATION' ||
      status === 'N/A' ||
      !status
    ) {
      if (db.migrationTaskId) {
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm bg-cyan-50 text-cyan-700 border border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-200 dark:border-cyan-800/50">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            In progress
          </span>
        );
      }
      // Truly no migration -> neutral badge
      return (
        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-700 shadow-sm">
          N/A
        </span>
      );
    }

    const config = statusConfig[status];

    // Fallback if status not in config
    if (!config) {
      return (
        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-gray-50 text-gray-500 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-700 shadow-sm">
          {rawStatus}
        </span>
      );
    }

    return (
      <div className="flex flex-col gap-1.5 items-start">
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${config.color}`}
        >
          {config.icon}
          {config.label}
        </span>
        {status === 'FAILED' && db.errorMessage && (
          <span
            className="text-[10px] text-rose-500 dark:text-rose-400 font-medium italic max-w-[180px] leading-tight"
            title={db.errorMessage}
          >
            {db.errorMessage}
          </span>
        )}
      </div>
    );
  };

  const renderManageDropdown = (db) => {
    const isOpen = openManageDropdown === db.id;

    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpenManageDropdown(isOpen ? null : db.id);
          }}
          className="px-4 py-2 text-xs border border-primaryBlue rounded-md text-primaryBlue hover:bg-primaryBlue/5 transition-colors whitespace-nowrap max-w-full overflow-hidden text-ellipsis"
        >
          Manage DB
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute right-0 bottom-full mb-2 md:bottom-auto md:top-full md:mt-2 w-56 bg-white dark:bg-slate-800 border dark:border-[#c4c4c432] rounded-md shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)] z-[100]"
          >
            {/* Check Migration Status - Show when RUNNING or when status is missing but task ID exists */}
            {(db.migrationStatus?.toString().toUpperCase() === 'RUNNING' ||
              db.migrationStatus?.toString().toUpperCase() === 'IN PROGRESS' ||
              (!db.migrationStatus && db.migrationTaskId)) && (
              <button
                onClick={() => handleCheckMigrationStatus(db)}
                className="w-full text-left px-4 py-3 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2"
              >
                <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                Check Migration Status
              </button>
            )}

            {/* View Details - Show for SUCCESS or FAILED */}
            {(db.migrationStatus?.toString().toUpperCase() === 'SUCCESS' ||
              db.migrationStatus?.toString().toUpperCase() === 'COMPLETED' ||
              db.migrationStatus?.toString().toUpperCase() === 'FAILED') && (
              <button
                onClick={() => handleViewDetails(db)}
                className="w-full text-left px-4 py-3 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2"
              >
                <Eye className="w-4 h-4 text-primaryBlue" />
                View Details
              </button>
            )}

            {/* Delete Database - Always visible */}
            <button
              onClick={() => {
                setSelectedDatabase(db);
                setDeleteDatabaseModal(true);
                setOpenManageDropdown(null);
              }}
              className="w-full text-left px-4 py-3 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2 text-red-500 border-t dark:border-[#c4c4c424]"
            >
              <img src={deleteIcon} alt="Delete" className="h-4" />
              Delete Database
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full text-sm px-4 md:px-10 py-6 min-h-screen relative">
      <div className="mb-6 crimson-font text-xl font-medium text-primaryBlue">
        Managed Databases
      </div>

      <div className="w-full flex justify-between items-center gap-4 lg:flex-row flex-col">
        <div className="w-full md:w-[initial] grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
          <div className="md:col-span-3">
            <Select
              label="Filter DB Servers"
              name="dbservers"
              options={options}
            />
          </div>
          <div className="md:col-span-4">
            <input
              type="search"
              value={searchText}
              onChange={handleSearchChange}
              placeholder="Search Databases..."
              className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <div
            className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer w-fit"
            onClick={toggleFlavorModal}
          >
            Create Managed Database
          </div>
          <div
            className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer w-fit"
            onClick={() => setMigrateFlavorModal(true)}
          >
            Migrate Database
          </div>
        </div>
      </div>

      <div className="w-full rounded-md dark:text-white overflow-x-auto min-h-[400px] pb-48">
        <table className="w-full text-sm text-left">
          <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
            <tr>
              <th scope="col" className="px-6 py-4 whitespace-nowrap w-5">
                #
              </th>
              <th scope="col" className="px-6 py-4 whitespace-nowrap max-w-52">
                Database Name
              </th>
              <th scope="col" className="px-6 py-4 whitespace-nowrap">
                IP Address
              </th>
              <th scope="col" className="px-6 py-4 whitespace-nowrap">
                Engine
              </th>
              <th scope="col" className="px-6 py-4 whitespace-nowrap">
                Server Type
              </th>
              <th scope="col" className="px-6 py-4 whitespace-nowrap">
                Status
              </th>
              <th scope="col" className="px-6 py-4 whitespace-nowrap">
                Migration Status
              </th>
              <th scope="col" className="px-6 py-4 whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className=" bg-white dark:bg-slate-800 text-black/50 dark:text-white/50 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
            {currentEntries.map((db, index) => (
              <tr
                key={db.id}
                className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
              >
                <td className="text-start py-4 px-6 w-5">
                  {(currentPage - 1) * entriesPerPage + (index + 1)}
                </td>
                <td className="text-start py-4 px-6 truncate max-w-52 font-medium">
                  {db.name}
                </td>
                <td className="text-start py-4 px-6 font-mono text-sm">
                  {db.ip}
                </td>
                <td className="text-start py-4 px-6">{db.engine}</td>
                <td className="text-start py-4 px-6">{db.serverType}</td>
                <td className="text-start py-4 px-6">
                  <div className="flex items-center gap-2">
                    {renderStatus(db)}
                    {/* Credentials Action - Only active if migration is complete or N/A */}
                    {(db.status === 'Completed' || db.status === 'Failed') && (
                      <button
                        onClick={() => handleViewDetails(db)}
                        disabled={
                          db.status === 'Completed' &&
                          !db.credentialsViewed &&
                          db.migrationStatus &&
                          db.migrationStatus?.toString().toUpperCase() !==
                            'COMPLETED' &&
                          db.migrationStatus?.toString().toUpperCase() !==
                            'SUCCESS' &&
                          db.migrationStatus?.toString().toUpperCase() !==
                            'NO MIGRATION' &&
                          db.migrationStatus?.toString().toUpperCase() !==
                            'NO_MIGRATION' &&
                          db.migrationStatus?.toString().toUpperCase() !== 'N/A'
                        }
                        className={`p-1 rounded-full transition-all shadow-sm border group flex items-center gap-1 ${
                          db.status === 'Completed'
                            ? db.credentialsViewed
                              ? 'bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-100 cursor-pointer'
                              : db.migrationStatus &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'COMPLETED' &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'SUCCESS' &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'NO MIGRATION' &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'NO_MIGRATION' &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'N/A'
                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100 cursor-pointer'
                            : 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100 cursor-pointer'
                        }`}
                        title={
                          db.status === 'Failed'
                            ? 'View Error Details'
                            : db.credentialsViewed
                              ? 'Credentials already viewed (Security Notice)'
                              : db.migrationStatus &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'COMPLETED' &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'SUCCESS' &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'NO MIGRATION' &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'NO_MIGRATION' &&
                                  db.migrationStatus
                                    ?.toString()
                                    .toUpperCase() !== 'N/A'
                                ? 'Credentials available after migration completes'
                                : 'View Credentials (One-time only)'
                        }
                      >
                        {db.status === 'Failed' ? (
                          <AlertTriangle className="w-3 h-3" />
                        ) : db.credentialsViewed ? (
                          <EyeOff className="w-3 h-3" />
                        ) : (
                          <Eye className="w-3 h-3" />
                        )}
                      </button>
                    )}
                  </div>
                </td>
                <td className="text-start py-4 px-6">
                  {renderMigrationStatus(db)}
                </td>
                <td className="text-start py-4 px-6">
                  {renderManageDropdown(db)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4 dark:text-white">
        <p className="text-gray-400 text-sm">
          Showing{' '}
          <span className="font-medium text-black dark:text-white">
            {(currentPage - 1) * entriesPerPage + 1}
          </span>{' '}
          to{' '}
          <span className="font-medium text-black dark:text-white">
            {Math.min(currentPage * entriesPerPage, filteredDatabases.length)}
          </span>{' '}
          of{' '}
          <span className="font-medium text-black dark:text-white">
            {filteredDatabases.length}
          </span>{' '}
          results
        </p>
        <div className="flex gap-3">
          <button
            className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
              currentPage === 1
                ? 'opacity-50 cursor-not-allowed'
                : 'opacity-100'
            }`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <button
            className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
              currentPage === totalPages
                ? 'opacity-50 cursor-not-allowed'
                : 'opacity-100'
            }`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Flavor Modal */}
      {flavorModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Select Flavor</p>
              <span onClick={toggleFlavorModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <Select
              label="Flavor:"
              name="flavor"
              options={flavorOptions}
              valueKey="value"
              labelKey="label"
              value={selectedFlavor}
             onChange={(e) => {
              const newFlavor =
                e.target.value === '' ? '' : Number(e.target.value);

              setSelectedFlavor(newFlavor);
              createDatabase.setFieldValue('flavor_id', newFlavor);
              toggleFlavorModal();
              toggleConfigModal();
            }}
            />
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {configModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create Managed Database</p>
              <span onClick={toggleConfigModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>

            {/* Display Selected Flavor Details */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-primaryBlue">
                Selected Flavor Details:
              </p>
              {(() => {
                if (!selectedFlavor || selectedFlavor === '') {
                  return (
                    <p className="text-sm text-gray-500">No flavor selected</p>
                  );
                }
                if (selectedFlavor === 'custom') {
                  return (
                    <p className="text-sm text-gray-500">
                      Custom flavor configuration selected.
                    </p>
                  );
                }

                const flavor = vpsMapList.find(
                  (vps) => vps.flavor_id === Number(selectedFlavor),
                );
                if (!flavor) {
                  return (
                    <p className="text-sm text-gray-500">Flavor not found</p>
                  );
                }
                return (
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <p>
                      <strong>Name:</strong> {flavor.name}
                    </p>
                    <p>
                      <strong>RAM:</strong> {flavor.ram}
                    </p>
                    <p>
                      <strong>CPU:</strong> {flavor.vcpus}
                    </p>
                    <p>
                      <strong>Storage:</strong> {flavor.disk}
                    </p>
                    <p>
                      <strong>Price:</strong>{' '}
                      {currency === 'NGN'
                        ? `N${flavor.naira_price}/mo`
                        : `$${flavor.dollar_price}/mo`}
                    </p>
                  </div>
                );
              })()}
            </div>

            <form className="grid gap-4 py-6">
              <Select
                label="Database Engine:"
                name="databaseEngine"
                options={[{ value: 'MySQL', label: 'MySQL' }]}
                valueKey="value"
                labelKey="label"
                value={createDatabase.values.databaseEngine}
                onChange={createDatabase.handleChange}
                disabled
              />
              <Input
                label="Database Name:"
                type="text"
                name="databaseName"
                value={createDatabase.values.databaseName}
                onChange={createDatabase.handleChange}
                onBlur={createDatabase.handleBlur}
                onError={
                  createDatabase.touched.databaseName &&
                  createDatabase.errors.databaseName
                    ? createDatabase.errors.databaseName
                    : null
                }
              />
              <Select
                label="Database Type:"
                name="databaseType"
                options={[
                  { value: '', label: 'Select Database Type' },
                  { value: 'mysql', label: 'MySQL' },
                ]}
                valueKey="value"
                labelKey="label"
                value={createDatabase.values.databaseType}
                onChange={createDatabase.handleChange}
                onError={
                  createDatabase.touched.databaseType &&
                  createDatabase.errors.databaseType
                    ? createDatabase.errors.databaseType
                    : null
                }
              />
              <Input
                label="Database Username:"
                type="text"
                name="databaseUsername"
                value={createDatabase.values.databaseUsername}
                onChange={createDatabase.handleChange}
                onBlur={createDatabase.handleBlur}
                onError={
                  createDatabase.touched.databaseUsername &&
                  createDatabase.errors.databaseUsername
                    ? createDatabase.errors.databaseUsername
                    : null
                }
              />
              <Input
                label="Database Password:"
                type={'password'}
                isPassword={'true'}
                name="databasePassword"
                value={createDatabase.values.databasePassword}
                onChange={createDatabase.handleChange}
                onBlur={createDatabase.handleBlur}
                onError={
                  createDatabase.touched.databasePassword &&
                  createDatabase.errors.databasePassword
                    ? createDatabase.errors.databasePassword
                    : null
                }
              />
              <Input
                label="Confirm Database Password:"
                type={'password'}
                isPassword={'true'}
                name="confirmDatabasePassword"
                value={createDatabase.values.confirmDatabasePassword}
                onChange={createDatabase.handleChange}
                onBlur={createDatabase.handleBlur}
                onError={
                  createDatabase.touched.confirmDatabasePassword &&
                  createDatabase.errors.confirmDatabasePassword
                    ? createDatabase.errors.confirmDatabasePassword
                    : null
                }
              />

              <Select
                label="Management Interface:"
                name="guiChoice"
                options={[
                  { value: '', label: 'Select GUI Choice' },
                  {
                    value: 'none',
                    label:
                      'None (Direct connection only) \n Command line access only',
                  },
                  {
                    value: 'phpmyadmin',
                    label:
                      'Phpmyadmin (Web-based MySQL administration (MySQL Only))',
                  },
                  {
                    value: 'adminer',
                    label: 'Adminer (Lightweight database management tool)',
                  },
                ]}
                valueKey="value"
                labelKey="label"
                value={createDatabase.values.guiChoice}
                onChange={createDatabase.handleChange}
                onError={
                  createDatabase.touched.guiChoice &&
                  createDatabase.errors.guiChoice
                    ? createDatabase.errors.guiChoice
                    : null
                }
              />
              <Select
                label="Payment:"
                name="payment"
                options={paymentOptions}
                valueKey="value"
                labelKey="label"
                value={createDatabase.values.payment}
                onBlur={createDatabase.handleBlur}
                onChange={createDatabase.handleChange}
                onError={
                  createDatabase.touched.payment &&
                  createDatabase.errors.payment
                    ? createDatabase.errors.payment
                    : null
                }
              />
              <div className="flex justify-between items-center gap-2">
                <div>
                  <div className="text-xs">Estimated cost:</div>
                  <div className="font-semibold crimson-font text-primaryBlue text-lg">
                    {
                      calculateEstimatedCost(
                        createDatabase.values.flavor_id,
                        currency,
                        createDatabase.values.payment,
                      ).formatted
                    }
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (isDbLimitReached) {
                      toast.warning("You have reached your limit of 10 Databases. Please contact support to increase your limit.", { theme: "colored" });
                      return;
                    }
                    createDatabase.handleSubmit();
                  }}
                  className={`text-white text-sm px-6 py-4 rounded-sm ${isDbLimitReached ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1DA1F2] cursor-pointer'}`}
                  disabled={
                    !createDatabase.isValid || createDatabase.isSubmitting || isDbLimitReached
                  }
                >
                  Create Database
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced Database Credentials Modal */}
      {credentialsModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-xl p-6 md:p-8 w-full md:w-2/3 max-w-2xl max-h-[90%] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-[#c4c4c432] pb-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-orange-500" />
                <p className="text-primaryBlue font-semibold text-lg">
                  Database Credentials
                </p>
              </div>
              <button
                onClick={handleCredentialsModalClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <img alt="Close" src={close} className="h-5 w-5" />
              </button>
            </div>

            {/* Critical Security Warning */}
            <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-400 p-4 mb-6">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-orange-800 dark:text-orange-200 mb-1">
                    ⚠️ CRITICAL SECURITY NOTICE
                  </p>
                  <p className="text-orange-700 dark:text-orange-300 mb-2 font-bold underline">
                    NOTE: Credentials can only be viewed once. Save them
                    immediately!
                  </p>
                  <p className="text-orange-700 dark:text-orange-300 mb-2">
                    After closing this modal, these credentials will be
                    permanently removed for security reasons.
                  </p>
                  <p className="text-orange-700 dark:text-orange-300 font-medium">
                    Store them securely in a password manager or encrypted
                    location.
                  </p>
                </div>
              </div>
            </div>

            {/* GUI Type Banner */}
            {serverCredentials && (
              <div
                className={`mb-4 p-3 rounded-lg border-l-4 ${
                  serverCredentials.isPhpMyAdmin
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-400'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-400'
                }`}
              >
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">Management Interface:</span>{' '}
                  {serverCredentials.isPhpMyAdmin
                    ? 'PhpMyAdmin (Web Interface)'
                    : 'Direct Connection Only'}
                </p>
                {serverCredentials.isPhpMyAdmin && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    You can access your database through the web interface using
                    the reset URL below.
                  </p>
                )}
              </div>
            )}

            {/* Credentials Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                  </span>
                  Database Credentials
                </h3>
                <button
                  onClick={copyAllCredentials}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors text-sm font-medium"
                >
                  <Copy className="w-4 h-4" />
                  Copy All
                </button>
              </div>

              {serverCredentials ? (
                <div className="grid gap-4">
                  {/* Host / Web Interface URL - Prominent for PhpMyAdmin */}
                  {serverCredentials.isPhpMyAdmin ? (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          Web Interface URL (PhpMyAdmin)
                        </label>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={openWebInterfaceUrl}
                            className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 transition-colors"
                            title="Open PhpMyAdmin in new tab"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              copyToClipboard(
                                serverCredentials.host,
                                'Web Interface URL',
                              )
                            }
                            className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-blue-900 dark:text-blue-100 font-mono bg-white dark:bg-blue-900/30 p-2 rounded border border-blue-200 dark:border-blue-600">
                        {serverCredentials.host}
                      </div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                        Click the external link icon to open PhpMyAdmin in a new
                        tab
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Host
                        </label>
                        <button
                          onClick={() =>
                            copyToClipboard(serverCredentials.host, 'Host')
                          }
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-700 p-2 rounded border">
                        {serverCredentials.host}
                      </div>
                    </div>
                  )}

                  {/* Port - Only show for direct connections */}
                  {!serverCredentials.isPhpMyAdmin && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Port
                        </label>
                        <button
                          onClick={() =>
                            copyToClipboard(serverCredentials.port, 'Port')
                          }
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-700 p-2 rounded border">
                        {serverCredentials.port}
                      </div>
                    </div>
                  )}

                  {/* Database */}
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Database
                      </label>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            serverCredentials.database,
                            'Database',
                          )
                        }
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-700 p-2 rounded border">
                      {serverCredentials.database}
                    </div>
                  </div>

                  {/* Username */}
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Username
                      </label>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            serverCredentials.username,
                            'Username',
                          )
                        }
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-700 p-2 rounded border">
                      {serverCredentials.username}
                    </div>
                  </div>

                  {/* Password */}
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Password
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            setCredentialsVisibility((prev) => ({
                              ...prev,
                              password: !prev.password,
                            }))
                          }
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                          {credentialsVisibility.password ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              serverCredentials.password,
                              'Password',
                            )
                          }
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-700 p-2 rounded border">
                      {credentialsVisibility.password
                        ? serverCredentials.password
                        : '•'.repeat(serverCredentials.password.length)}
                    </div>
                  </div>

                  {/* Reset URL - Only for PhpMyAdmin (Secondary, less prominent) */}
                  {serverCredentials.isPhpMyAdmin &&
                    serverCredentials.resetUrl && (
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-[#c4c4c432]">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Reset URL (For Password Reset)
                          </label>
                          <button
                            onClick={() =>
                              copyToClipboard(
                                serverCredentials.resetUrl,
                                'Reset URL',
                              )
                            }
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-700 p-2 rounded border">
                          {serverCredentials.resetUrl}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Use this URL if you need to reset your database
                          password
                        </p>
                      </div>
                    )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    No credentials available. Debug info:{' '}
                    {JSON.stringify(serverCredentials)}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-center mt-8 pt-6 border-t border-gray-200 dark:border-[#c4c4c432]">
              <button
                onClick={handleCredentialsModalClose}
                className="bg-[#1DA1F2] hover:bg-[#1a91da] text-white text-sm px-8 py-3 rounded-lg transition-colors font-medium"
              >
                Close & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {migrationSuccessModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4 ">
          <div className="bg-white dark:bg-[#141D26] rounded-xl p-4 md:p-10 w-full md:w-1/3">
            <div className="flex items-center justify-between mb-4 border-b border-gray-200 dark:border-[#c4c4c432] pb-4">
              <p className="text-primaryBlue font-semibold">Migration Status</p>
              <span
                onClick={handleMigrationSuccessModalClose}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <div className="py-6 flex flex-col justify-center items-center gap-6">
              <div className="w-20 h-20 bg-green-400 rounded-full p-3 flex flex-col justify-center items-center text-green-600">
                <CircleCheckBig className="w-12 h-12 text-white" />
              </div>
              <p className="text-gray-700 dark:text-gray-300 text-center mb-6">
                Database created successfully! Migration is running in the
                background.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={handleMigrationSuccessModalClose}
                  className="bg-[#1DA1F2] text-white text-sm px-6 py-3 rounded-sm hover:bg-[#1a91da] transition-colors"
                >
                  View Credentials
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Migration Error Modal */}
      {errorDetailsModal && selectedDbForError && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-xl p-6 md:p-8 w-full md:w-1/2 max-w-2xl">
            <div className="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-[#c4c4c432] pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-primaryBlue font-semibold text-lg">
                  Migration Failed
                </h2>
              </div>
              <button
                onClick={() => {
                  setErrorDetailsModal(false);
                  setSelectedDbForError(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <img src={close} alt="Close" className="h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  Error Message
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {selectedDbForError.errorMessage || 'Unknown error occurred'}
                </p>
              </div>

              {selectedDbForError.dbDetails && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                    Additional Details
                  </p>
                  <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {typeof selectedDbForError.dbDetails === 'string'
                      ? selectedDbForError.dbDetails
                      : JSON.stringify(selectedDbForError.dbDetails, null, 2)}
                  </pre>
                </div>
              )}

              <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                  Suggested Actions
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                  <li>Verify source database credentials</li>
                  <li>Check network connectivity</li>
                  <li>Ensure source database is accessible</li>
                  <li>Contact support if the issue persists</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-[#c4c4c432]">
              <button
                onClick={() => {
                  setErrorDetailsModal(false);
                  setSelectedDbForError(null);
                }}
                className="bg-primaryBlue hover:bg-blue-700 text-white text-sm px-6 py-2 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Feedback Modal */}
      {securityFeedbackModal && selectedDbForSecurity && (
        <Modal
          size="md"
          title="Security Notice"
          isOpen={securityFeedbackModal}
          onClose={() => setSecurityFeedbackModal(false)}
        >
          <div className="flex flex-col gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg flex gap-3">
              <AlertTriangle className="w-6 h-6 text-blue-600 dark:text-blue-400 shrink-0" />
              <div>
                <p className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-1">
                  Credentials Already Viewed
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  You have already viewed the secure credentials for{' '}
                  <strong>{selectedDbForSecurity.name}</strong>. For your
                  security, we only display these details once during the
                  initial setup.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/40 p-4 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                If you did not save these credentials or need to access the
                database again, please use the <strong>Reset Password</strong>{' '}
                feature in the management console or contact our support team.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setSecurityFeedbackModal(false)}
                className="bg-primaryBlue hover:bg-blue-700 text-white font-medium py-2 px-8 rounded-lg transition-colors w-full sm:w-auto shadow-sm"
              >
                I Understand
              </button>
            </div>
          </div>
        </Modal>
      )}

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setIsPaymentForMigration(false);
        }}
        currency={currency}
        totalPrice={
          isPaymentForMigration
            ? calculateEstimatedCost(
                migrateDatabase.values.flavor_id,
                currency,
                migrateDatabase.values.payment,
              ).numeric
            : calculateEstimatedCost(
                createDatabase.values.flavor_id,
                currency,
                createDatabase.values.payment,
              ).numeric
        }
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={async () => {
          // Refresh wallet balance after payment confirmation
          await dispatch(getUserVirtualAccount());
          // Then proceed with database creation or migration
          if (isPaymentForMigration) {
            migrateDatabase.handleSubmit();
          } else {
            createDatabase.handleSubmit();
          }
        }}
      />

      {/* Migration Flavor Modal */}
      {migrateFlavorModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-primaryBlue font-semibold">
                  Select Flavor for Migration
                </p>
                <span
                  onClick={toggleMigrateFlavorModal}
                  className="cursor-pointer"
                >
                  <img alt="" src={close} className="h-5" />
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Select the target database flavor. A minimum of 4 GB RAM is
                recommended for faster migration.
              </p>
            </div>
            <Select
              label="Flavor:"
              name="flavor"
              options={flavorOptions}
              valueKey="value"
              labelKey="label"
              value={selectedMigrateFlavor}
              onChange={async (e) => {
                const newFlavor = e.target.value === '' ? '' : e.target.value; // Keep as string for Yup validation consistency
                setSelectedMigrateFlavor(newFlavor);
                migrateDatabase.setFieldValue('flavor_id', newFlavor);
                toggleMigrateFlavorModal();
                // Refresh wallet balance before opening config modal
                await dispatch(getUserVirtualAccount());
                toggleMigrateConfigModal(newFlavor);
              }}
            />
          </div>
        </div>
      )}

      {/* Migration Configuration Modal with Multi-Step Form */}
      {migrateConfigModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/2 max-h-[90%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Migrate Database</p>
              <span
                onClick={toggleMigrateConfigModal}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>

            {/* Step Indicator */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`flex-1 ${migrationStep >= 1 ? 'text-primaryBlue' : 'text-gray-400'}`}
                >
                  <div
                    className={`h-2 rounded-full ${migrationStep >= 1 ? 'bg-primaryBlue' : 'bg-gray-300'}`}
                  ></div>
                  <p className="text-xs mt-2 text-center">
                    Step 1: Database Setup
                  </p>
                </div>
                <div
                  className={`flex-1 mx-2 ${migrationStep >= 2 ? 'text-primaryBlue' : 'text-gray-400'}`}
                >
                  <div
                    className={`h-2 rounded-full ${migrationStep >= 2 ? 'bg-primaryBlue' : 'bg-gray-300'}`}
                  ></div>
                  <p className="text-xs mt-2 text-center">
                    Step 2: Source DB Info
                  </p>
                </div>
                <div
                  className={`flex-1 ${migrationStep >= 3 ? 'text-primaryBlue' : 'text-gray-400'}`}
                >
                  <div
                    className={`h-2 rounded-full ${migrationStep >= 3 ? 'bg-primaryBlue' : 'bg-gray-300'}`}
                  ></div>
                  <p className="text-xs mt-2 text-center">
                    Step 3: Migration Settings
                  </p>
                </div>
              </div>
            </div>

            {/* Display Selected Flavor Details */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-primaryBlue">
                Selected Flavor Details:
              </p>
              {(() => {
                if (!selectedMigrateFlavor || selectedMigrateFlavor === '') {
                  return (
                    <p className="text-sm text-gray-500">No flavor selected</p>
                  );
                }
                if (selectedMigrateFlavor === 'custom') {
                  return (
                    <p className="text-sm text-gray-500">
                      Custom flavor configuration selected.
                    </p>
                  );
                }

                const flavor = vpsMapList.find(
                  (vps) => vps.flavor_id === Number(selectedMigrateFlavor),
                );
                if (!flavor) {
                  return (
                    <p className="text-sm text-gray-500">Flavor not found</p>
                  );
                }
                return (
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <p>
                      <strong>Name:</strong> {flavor.name}
                    </p>
                    <p>
                      <strong>RAM:</strong> {flavor.ram}
                    </p>
                    <p>
                      <strong>CPU:</strong> {flavor.vcpus}
                    </p>
                    <p>
                      <strong>Storage:</strong> {flavor.disk}
                    </p>
                    <p>
                      <strong>Price:</strong>{' '}
                      {currency === 'NGN'
                        ? `N${flavor.naira_price}/mo`
                        : `$${flavor.dollar_price}/mo`}
                    </p>
                  </div>
                );
              })()}
            </div>

            <form className="grid gap-4 py-6">
              {/* Step 1: Database Setup */}
              {migrationStep === 1 && (
                <>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 mb-4">
                    <p className="text-sm text-blue-800 dark:text-blue-200 font-semibold mb-2">
                      ⚠️ Important Note
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      The database name provided must match the name of the
                      database on the source database engine from which the
                      migration will be performed.
                    </p>
                  </div>
                  <Select
                    label="Database Engine:"
                    name="databaseEngine"
                    options={[{ value: 'MySQL', label: 'MySQL' }]}
                    valueKey="value"
                    labelKey="label"
                    value={migrateDatabase.values.databaseEngine}
                    onChange={migrateDatabase.handleChange}
                    disabled
                  />
                  <Input
                    label="Database Name (Source DB Name):"
                    type="text"
                    name="databaseName"
                    value={migrateDatabase.values.databaseName}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    onError={
                      migrateDatabase.touched.databaseName &&
                      migrateDatabase.errors.databaseName
                        ? migrateDatabase.errors.databaseName
                        : null
                    }
                  />
                  <div className="hidden">
                    <Select
                      label="Database Type:"
                      name="databaseType"
                      options={[{ value: 'mysql', label: 'MySQL' }]}
                      valueKey="value"
                      labelKey="label"
                      value={migrateDatabase.values.databaseType || 'mysql'}
                      onChange={migrateDatabase.handleChange}
                    />
                  </div>

                  {/* Added visible feedback for selected flavor */}

                  {/* flavor_id (Hidden but required by schema) */}
                  <div className="hidden">
                    <input
                      name="flavor_id"
                      value={migrateDatabase.values.flavor_id}
                      onChange={migrateDatabase.handleChange}
                    />
                  </div>
                  <Input
                    label="Target Database Username:"
                    type="text"
                    name="databaseUsername"
                    value={migrateDatabase.values.databaseUsername}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    onError={
                      migrateDatabase.touched.databaseUsername &&
                      migrateDatabase.errors.databaseUsername
                        ? migrateDatabase.errors.databaseUsername
                        : null
                    }
                  />
                  <Input
                    label="Target Database Password:"
                    type={'password'}
                    isPassword={'true'}
                    name="databasePassword"
                    value={migrateDatabase.values.databasePassword}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    onError={
                      migrateDatabase.touched.databasePassword &&
                      migrateDatabase.errors.databasePassword
                        ? migrateDatabase.errors.databasePassword
                        : null
                    }
                  />
                  <Input
                    label="Confirm Database Password:"
                    type={'password'}
                    isPassword={'true'}
                    name="confirmDatabasePassword"
                    value={migrateDatabase.values.confirmDatabasePassword}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    onError={
                      migrateDatabase.touched.confirmDatabasePassword &&
                      migrateDatabase.errors.confirmDatabasePassword
                        ? migrateDatabase.errors.confirmDatabasePassword
                        : null
                    }
                  />
                  <Select
                    label="Management Interface:"
                    name="guiChoice"
                    options={[
                      { value: '', label: 'Select GUI Choice' },
                      {
                        value: 'none',
                        label:
                          'None (Direct connection only) \n Command line access only',
                      },
                      {
                        value: 'phpmyadmin',
                        label:
                          'Phpmyadmin (Web-based MySQL administration (MySQL Only))',
                      },
                      {
                        value: 'adminer',
                        label: 'Adminer (Lightweight database management tool)',
                      },
                    ]}
                    valueKey="value"
                    labelKey="label"
                    value={migrateDatabase.values.guiChoice}
                    onChange={migrateDatabase.handleChange}
                    onError={
                      migrateDatabase.touched.guiChoice &&
                      migrateDatabase.errors.guiChoice
                        ? migrateDatabase.errors.guiChoice
                        : null
                    }
                  />
                  <Select
                    label="Payment:"
                    name="payment"
                    options={paymentOptions}
                    valueKey="value"
                    labelKey="label"
                    value={migrateDatabase.values.payment}
                    onBlur={migrateDatabase.handleBlur}
                    onChange={migrateDatabase.handleChange}
                    onError={
                      migrateDatabase.touched.payment &&
                      migrateDatabase.errors.payment
                        ? migrateDatabase.errors.payment
                        : null
                    }
                  />
                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      onClick={async () => {
                        console.log('--- Migration Next Button Diagnostic ---');
                        const v = migrateDatabase.values;
                        const s = selectedMigrateFlavor;

                        console.log('Step:', migrationStep);
                        console.log('Form Values:', v);
                        console.log('Component State Flavor:', s);

                        // 1. Recovery: Sync critical fields if missing
                        let finalFlavor = v.flavor_id || s || '';
                        if (
                          finalFlavor &&
                          (!v.flavor_id || v.flavor_id === '')
                        ) {
                          console.log(
                            'SYNC: Restoring flavor_id from component state',
                          );
                          await migrateDatabase.setFieldValue(
                            'flavor_id',
                            finalFlavor.toString(),
                            false,
                          );
                        }

                        // Force databaseType if missing
                        if (!v.databaseType) {
                          await migrateDatabase.setFieldValue(
                            'databaseType',
                            'mysql',
                            false,
                          );
                        }

                        // 2. Manual Field-by-Field Check for Step 1
                        const step1Errors = [];

                        if (!v.databaseName) step1Errors.push('Database Name');
                        if (!v.databaseUsername)
                          step1Errors.push('Database Username');
                        if (!v.databasePassword)
                          step1Errors.push('Database Password');
                        if (v.databasePassword !== v.confirmDatabasePassword)
                          step1Errors.push('Password Mismatch');
                        if (!v.guiChoice)
                          step1Errors.push('Management Interface');
                        if (!v.payment) step1Errors.push('Payment Method');
                        if (!finalFlavor) step1Errors.push('Flavor selection');

                        // 3. Optional: Trigger Yup for remaining fields/patterns
                        // But we trust our manual checks more for Step 1 "required" status
                        const formikErrors =
                          await migrateDatabase.validateForm();
                        console.log('Formik Errors (remaining):', formikErrors);

                        // Mark as touched for UI
                        migrateDatabase.setTouched({
                          databaseName: true,
                          databaseUsername: true,
                          databasePassword: true,
                          confirmDatabasePassword: true,
                          guiChoice: true,
                          payment: true,
                          flavor_id: true,
                        });

                        if (step1Errors.length === 0) {
                          console.log('Step 1 Validated Manually. Proceeding.');
                          setMigrationStep(2);
                        } else {
                          console.log(
                            'Step 1 Manual Validation Failed:',
                            step1Errors,
                          );
                          toast.error(
                            `Please complete these Step 1 fields: ${step1Errors.join(', ')}`,
                          );
                        }
                      }}
                      className="bg-[#1DA1F2] text-white text-sm px-6 py-3 rounded-sm"
                    >
                      Next: Source DB Info
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: Source Database Information */}
              {migrationStep === 2 && (
                <>
                  <Input
                    label="Source Database IP Address:"
                    type="text"
                    name="source_db_ip"
                    value={migrateDatabase.values.source_db_ip}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    placeholder="172.168.192.304"
                    onError={
                      migrateDatabase.touched.source_db_ip &&
                      migrateDatabase.errors.source_db_ip
                        ? migrateDatabase.errors.source_db_ip
                        : null
                    }
                  />
                  <Input
                    label="Source Database Port:"
                    type="number"
                    name="source_db_port"
                    value={migrateDatabase.values.source_db_port}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    placeholder="3306"
                    onError={
                      migrateDatabase.touched.source_db_port &&
                      migrateDatabase.errors.source_db_port
                        ? migrateDatabase.errors.source_db_port
                        : null
                    }
                  />
                  <Input
                    label="Source Database Username:"
                    type="text"
                    name="source_db_user"
                    value={migrateDatabase.values.source_db_user}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    placeholder="root"
                    onError={
                      migrateDatabase.touched.source_db_user &&
                      migrateDatabase.errors.source_db_user
                        ? migrateDatabase.errors.source_db_user
                        : null
                    }
                  />
                  <Input
                    label="Source Database Password:"
                    type={'password'}
                    isPassword={'true'}
                    name="source_db_password"
                    value={migrateDatabase.values.source_db_password}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    placeholder="testing123"
                    onError={
                      migrateDatabase.touched.source_db_password &&
                      migrateDatabase.errors.source_db_password
                        ? migrateDatabase.errors.source_db_password
                        : null
                    }
                  />

                  {/* Database Connection Check Section */}
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Check Database Connection
                      </h3>
                      {connectionCheckStatus === 'SUCCESS' && (
                        <CircleCheckBig className="w-5 h-5 text-green-500" />
                      )}
                      {connectionCheckStatus === 'FAILED' && (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      )}
                    </div>

                    {connectionCheckStatus === 'SUCCESS' && databaseInfo && (
                      <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                        <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                          ✓ Connection verified successfully
                        </p>
                        <div className="text-sm text-green-700 dark:text-green-300 space-y-1">
                          <p>
                            <strong>Database:</strong> {databaseInfo.database}
                          </p>
                          <p>
                            <strong>Table Count:</strong>{' '}
                            {databaseInfo.table_count}
                          </p>
                          <p>
                            <strong>Total Rows:</strong>{' '}
                            {databaseInfo.total_rows?.toLocaleString()}
                          </p>
                          <p>
                            <strong>Size:</strong> {databaseInfo.size_mb} MB
                          </p>
                        </div>
                      </div>
                    )}

                    {connectionCheckStatus === 'FAILED' &&
                      connectionCheckError && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                          <p className="text-sm font-medium text-red-800 dark:text-red-200">
                            ✗ Connection test failed
                          </p>
                          <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                            {connectionCheckError}
                          </p>
                        </div>
                      )}

                    <button
                      type="button"
                      onClick={handleCheckDatabaseConnection}
                      disabled={
                        isCheckingConnection ||
                        connectionCheckStatus === 'PENDING' ||
                        !migrateDatabase.values.source_db_ip ||
                        !migrateDatabase.values.source_db_port ||
                        !migrateDatabase.values.source_db_user ||
                        !migrateDatabase.values.source_db_password ||
                        !migrateDatabase.values.source_db_password ||
                        !migrateDatabase.values.databaseName ||
                        connectionCheckStatus === 'SUCCESS'
                      }
                      className={`w-full bg-blue-600 text-white text-sm px-6 py-3 rounded-sm flex items-center justify-center gap-2 ${
                        isCheckingConnection ||
                        connectionCheckStatus === 'PENDING' ||
                        !migrateDatabase.values.source_db_ip ||
                        !migrateDatabase.values.source_db_port ||
                        !migrateDatabase.values.source_db_user ||
                        !migrateDatabase.values.source_db_password ||
                        !migrateDatabase.values.databaseName ||
                        connectionCheckStatus === 'SUCCESS' // Disable if already successful
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-blue-700'
                      }`}
                    >
                      {isCheckingConnection ||
                      connectionCheckStatus === 'PENDING' ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {connectionCheckStatus === 'PENDING'
                            ? 'Checking connection...'
                            : 'Initiating check...'}
                        </>
                      ) : (
                        'Check database connectivity'
                      )}
                    </button>
                  </div>

                  <div className="flex justify-between mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setMigrationStep(1);
                        // Reset connection check when going back
                        setConnectionCheckStatus(null);
                        setDatabaseInfo(null);
                        setConnectionCheckError(null);
                      }}
                      className="bg-gray-500 text-white text-sm px-6 py-3 rounded-sm"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // Validate step 2 fields
                        const step2Fields = [
                          'source_db_ip',
                          'source_db_port',
                          'source_db_user',
                          'source_db_password',
                        ];
                        const touched = {};
                        step2Fields.forEach((field) => {
                          touched[field] = true;
                        });
                        migrateDatabase.setTouched(touched);

                        const step2Errors = step2Fields.filter(
                          (field) => migrateDatabase.errors[field],
                        );

                        // CRITICAL: Must have successful connection check before proceeding
                        if (step2Errors.length === 0) {
                          if (connectionCheckStatus !== 'SUCCESS') {
                            toast.error(
                              "You must verify the database connection before proceeding to migration! Click 'Check Database Connection' first.",
                            );
                            return;
                          }
                          setMigrationStep(3);
                        }
                      }}
                      className={`text-sm px-6 py-3 rounded-sm ${
                        connectionCheckStatus === 'SUCCESS'
                          ? 'bg-[#1DA1F2] text-white hover:bg-[#1a91da] cursor-pointer'
                          : 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-60'
                      }`}
                      disabled={connectionCheckStatus !== 'SUCCESS'}
                    >
                      Next: Migration Settings
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Migration Settings */}
              {migrationStep === 3 && (
                <>
                  <Select
                    label="Migration Type:"
                    name="migration_type"
                    options={[
                      { value: '', label: 'Select Migration Type' },
                      { value: 'test', label: 'Test' },
                      { value: 'live', label: 'Live' },
                    ]}
                    valueKey="value"
                    labelKey="label"
                    value={migrateDatabase.values.migration_type}
                    onChange={migrateDatabase.handleChange}
                    onBlur={migrateDatabase.handleBlur}
                    onError={
                      migrateDatabase.touched.migration_type &&
                      migrateDatabase.errors.migration_type
                        ? migrateDatabase.errors.migration_type
                        : null
                    }
                  />
                  <div className="flex justify-between items-center gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setMigrationStep(2)}
                      className="bg-gray-500 text-white text-sm px-6 py-3 rounded-sm"
                    >
                      Back
                    </button>
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="text-xs">Estimated cost:</div>
                        <div className="font-semibold crimson-font text-primaryBlue text-lg">
                          {
                            calculateEstimatedCost(
                              migrateDatabase.values.flavor_id,
                              currency,
                              migrateDatabase.values.payment,
                            ).formatted
                          }
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => migrateDatabase.handleSubmit()}
                        className="bg-[#1DA1F2] text-white text-sm px-6 py-4 rounded-sm"
                        disabled={
                          !migrateDatabase.isValid ||
                          migrateDatabase.isSubmitting
                        }
                      >
                        Start Migration
                      </button>
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      <Modal
        title="Delete Database"
        isVisible={deleteDatabaseModal}
        onClose={toggleDeleteDatabaseModal}
      >
        Are you sure you want to delete {selectedDatabase?.name}?
        <div className="flex justify-between pt-6">
          <button
            className="bg-red-500 text-white text-sm px-6 py-2 rounded-sm"
            onClick={() => deleteDatabase(selectedDatabase?.id)}
          >
            Yes
          </button>
          <button
            className="bg-[#1DA1F2] text-white text-sm px-6 py-2 rounded-sm"
            onClick={toggleDeleteDatabaseModal}
          >
            No
          </button>
        </div>
      </Modal>

      {/* Security Feedback Modal */}
      <Modal
        title="Security Notice"
        isVisible={securityFeedbackModal}
        onClose={() => setSecurityFeedbackModal(false)}
      >
        <div className="flex flex-col items-center text-center p-4">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
            <EyeOff className="w-8 h-8 text-orange-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
            Credentials Already Viewed
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-6 leading-relaxed">
            For security reasons, database credentials can only be displayed{' '}
            <b>once</b>. We do not store plain-text passwords after they have
            been retrieved.
          </p>
          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg w-full text-left mb-6 border border-gray-100 dark:border-gray-700">
            <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
              What can I do?
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc pl-4">
              <li>Check your password manager if you saved them previously.</li>
              <li>
                Reset the password if you have lost access (this may affect
                connected apps).
              </li>
              <li>Contact support if you need further assistance.</li>
            </ul>
          </div>
          <button
            onClick={() => setSecurityFeedbackModal(false)}
            className="bg-[#1DA1F2] hover:bg-[#1a91da] text-white font-medium py-2 px-6 rounded-lg transition-colors w-full sm:w-auto"
          >
            I Understand
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ManagedDatabases;
